/**
 * The body of a template, as `doc/v1` allows it (D10): paragraphs, bullet and
 * numbered lists of one level, bold / italic / underline, placeholders.
 *
 * BLOCKS ARE VISIBLE AS BLOCKS. Each one is its own framed row with a type
 * select ("Absatz", "Aufzählung", "Nummerierte Liste") and a remove button —
 * the body always keeps one block, a list always keeps one point, because the
 * server refuses an empty one. Nothing outside that grammar can be made here:
 * no heading, no link, no image, no HTML.
 *
 * A STRUCTURAL BLOCK IS SHOWN, NOT EDITED (D12a). Where the server fills a
 * part of the letter itself — the reminder's list of outstanding obligations —
 * the body shows a locked block saying what will stand there and where it
 * comes from. It has no type select, no remove button and no text field, and
 * nothing here adds one: the server names the block, the draft is born with
 * it, and the text before and after it is written in the blocks around it.
 *
 * FORMATTING ACTS ON THE TEXT THE CARET IS IN. The three buttons apply to the
 * selection of the active text field (or the whole field when nothing is
 * selected); their `aria-pressed` says whether that field carries the mark.
 * They keep the field's selection by not taking focus on mouse-down.
 */
import React from "react";
import { Bold, Italic, Lock, Trash2, Underline } from "lucide-react";

import AutomationTemplateField from "./AutomationTemplateField";
import { STRUCTURAL } from "./automationTemplateDoc";
import { structuralBlockLabel } from "./automationTemplateLabels";

const BLOCK_TYPE_OPTIONS = [
  { value: "paragraph", label: "Absatz" },
  { value: "bullet_list", label: "Aufzählung" },
  { value: "ordered_list", label: "Nummerierte Liste" },
];

const MARK_BUTTONS = [
  { mark: "strong", label: "Fett", Icon: Bold },
  { mark: "emphasis", label: "Kursiv", Icon: Italic },
  { mark: "underline", label: "Unterstrichen", Icon: Underline },
];

const SMALL_BUTTON =
  "inline-flex items-center gap-1.5 rounded-[8px] border border-border bg-card px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";

const ICON_BUTTON =
  "inline-flex h-8 w-8 items-center justify-center max-md:h-9 max-md:w-9 rounded-[8px] border border-border bg-card text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 aria-pressed:border-primary aria-pressed:bg-primary/10 aria-pressed:text-primary";

function StructuralBlock({ node, blockIndex }) {
  const { title, description } = structuralBlockLabel(node);
  return (
    <li
      aria-label={`Block ${blockIndex + 1}: ${title}`}
      className="flex min-w-0 flex-col gap-2 rounded-[12px] border border-border bg-card p-2.5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11.5px] font-medium text-muted-foreground">
          Block {blockIndex + 1}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          <Lock className="h-3 w-3" aria-hidden="true" />
          Automatisch
        </span>
      </div>
      <div className="flex flex-col gap-0.5 rounded-[10px] border border-dashed border-border bg-muted/40 px-3 py-2.5">
        <p className="m-0 text-[13px] font-semibold text-foreground">{title}</p>
        <p className="m-0 text-[12px] text-muted-foreground">{description}</p>
      </div>
    </li>
  );
}

export default function AutomationTemplateBodyEditor({
  body,
  activeMarks,
  marksEnabled,
  onToggleMark,
  onAddBlock,
  onRemoveBlock,
  onSetBlockType,
  onAddListItem,
  onRemoveListItem,
  fieldProps,
}) {
  const paragraphLabel = (blockIndex, itemIndex) =>
    itemIndex === null
      ? `Absatz ${blockIndex + 1}`
      : `Block ${blockIndex + 1}, Punkt ${itemIndex + 1}`;

  return (
    <div className="flex flex-col gap-3">
      <div role="toolbar" aria-label="Formatierung" className="flex flex-wrap items-center gap-1.5">
        {MARK_BUTTONS.map(({ mark, label, Icon }) => (
          <button
            key={mark}
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={activeMarks.includes(mark)}
            disabled={!marksEnabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onToggleMark(mark)}
            className={ICON_BUTTON}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </button>
        ))}
      </div>

      <ol aria-label="Inhaltsblöcke" className="m-0 flex list-none flex-col gap-2.5 p-0">
        {body.blocks.map((block, blockIndex) =>
          block.type === STRUCTURAL ? (
            <StructuralBlock key={block.id} node={block.node} blockIndex={blockIndex} />
          ) : (
            <li
              key={block.id}
              className="flex min-w-0 flex-col gap-2 rounded-[12px] border border-border bg-card p-2.5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-[11.5px] font-medium text-muted-foreground">
                  <span>Block {blockIndex + 1}</span>
                  <select
                    value={block.type}
                    onChange={(event) => onSetBlockType(block.id, event.target.value)}
                    aria-label={`Typ von Block ${blockIndex + 1}`}
                    className="rounded-[8px] border border-border bg-background px-2 py-1 text-[12px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-9"
                  >
                    {BLOCK_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => onRemoveBlock(block.id)}
                  disabled={body.blocks.length <= 1}
                  aria-label={`Block ${blockIndex + 1} entfernen`}
                  className={SMALL_BUTTON}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Entfernen
                </button>
              </div>

              {block.type === "paragraph" ? (
                <AutomationTemplateField
                  {...fieldProps(block.paragraph)}
                  paragraph={block.paragraph}
                  label={paragraphLabel(blockIndex, null)}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <ol className="m-0 flex list-none flex-col gap-2 p-0">
                    {block.items.map((item, itemIndex) => (
                      <li key={item.id} className="flex min-w-0 items-start gap-2">
                        <span
                          aria-hidden="true"
                          className="mt-2 w-5 shrink-0 text-right text-[12.5px] tabular-nums text-muted-foreground"
                        >
                          {block.type === "ordered_list" ? `${itemIndex + 1}.` : "•"}
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                          {item.paragraphs.map((paragraph) => (
                            <AutomationTemplateField
                              key={paragraph.id}
                              {...fieldProps(paragraph)}
                              paragraph={paragraph}
                              label={paragraphLabel(blockIndex, itemIndex)}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => onRemoveListItem(block.id, item.id)}
                          disabled={block.items.length <= 1}
                          aria-label={`Punkt ${itemIndex + 1} von Block ${blockIndex + 1} entfernen`}
                          className={`${ICON_BUTTON} mt-0.5 shrink-0`}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ol>
                  <div>
                    <button
                      type="button"
                      onClick={() => onAddListItem(block.id)}
                      className={SMALL_BUTTON}
                    >
                      Punkt hinzufügen
                    </button>
                  </div>
                </div>
              )}
            </li>
          )
        )}
      </ol>

      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => onAddBlock("paragraph")} className={SMALL_BUTTON}>
          Absatz hinzufügen
        </button>
        <button type="button" onClick={() => onAddBlock("bullet_list")} className={SMALL_BUTTON}>
          Aufzählung hinzufügen
        </button>
        <button type="button" onClick={() => onAddBlock("ordered_list")} className={SMALL_BUTTON}>
          Nummerierte Liste hinzufügen
        </button>
      </div>
    </div>
  );
}
